<template>
  <div class="report-page">
    <ReportHeaderTab />

    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h5 class="page-title">Sales By Category</h5>
        <p class="page-sub">Compare net sales, cost of goods and gross profit across product categories</p>
      </div>

      <div class="filters-wrap">

        <!-- From -->
        <div class="filter-group">
          <div class="filter-label">From</div>
          <div class="date-input-wrap">
            <font-awesome-icon icon="calendar" class="date-icon" />
            <Datepicker v-model="tableData.start_date" :enableTimePicker="false" class="date-picker" />
          </div>
        </div>

        <div class="filter-sep">—</div>

        <!-- To -->
        <div class="filter-group">
          <div class="filter-label">To</div>
          <div class="date-input-wrap">
            <font-awesome-icon icon="calendar" class="date-icon" />
            <Datepicker v-model="tableData.end_date" :enableTimePicker="false" class="date-picker" />
          </div>
        </div>

        <!-- Store -->
        <div class="filter-group">
          <div class="filter-label">Store</div>
          <Multiselect
            v-model="tableData.store"
            label="name"
            :options="getStore"
            placeholder="All Stores"
            :searchable="true"
            :create-option="false"
            :close-on-select="true"
            class="filter-multiselect"
          />
        </div>

        <!-- Search -->
        <div class="filter-group">
          <div class="filter-label">Search</div>
          <div class="search-wrap">
            <font-awesome-icon icon="search" class="search-icon" />
            <input
              type="text"
              class="search-input"
              placeholder="Search category..."
              v-model="query"
            />
          </div>
        </div>

        <!-- Apply -->
        <button class="btn-apply" :disabled="crudLoading" @click="fetchData()">
          <font-awesome-icon :icon="crudLoading ? 'spinner' : 'filter'" :spin="crudLoading" />
          {{ crudLoading ? 'Applying...' : 'Apply' }}
        </button>

        <!-- Export -->
        <div class="dropdown">
          <button class="btn-outline" @click="exportOpen = !exportOpen">
            <font-awesome-icon icon="download" /> Export
          </button>
          <ul class="dropdown-menu" :class="{ show: exportOpen }">
            <li>
              <a class="dropdown-item" href="#" @click.prevent="exportPdf">
                <font-awesome-icon icon="file-pdf" class="text-danger me-2" /> PDF
              </a>
            </li>
            <li>
              <a class="dropdown-item" href="#" @click.prevent="exportCsv">
                <font-awesome-icon icon="file-csv" class="text-success me-2" /> CSV
              </a>
            </li>
          </ul>
        </div>

      </div>
    </div>

    <!-- Summary cards -->
    <div class="summary-grid" v-if="details.length > 0 && !loading">
      <div class="summary-card">
        <div class="summary-icon summary-icon--blue">
          <font-awesome-icon icon="tags" />
        </div>
        <div>
          <div class="summary-label">Categories</div>
          <div class="summary-value">{{ pagination.total }}</div>
        </div>
      </div>
      <div class="summary-card">
        <div class="summary-icon summary-icon--indigo">
          <font-awesome-icon icon="boxes" />
        </div>
        <div>
          <div class="summary-label">Products Sold</div>
          <div class="summary-value">{{ total_product_sold }}</div>
        </div>
      </div>
      <div class="summary-card">
        <div class="summary-icon summary-icon--amber">
          <font-awesome-icon icon="receipt" />
        </div>
        <div>
          <div class="summary-label">Net Sales</div>
          <div class="summary-value">{{ company?.currency?.symbol }}{{ $filters.formatMoney(total_net_sales) }}</div>
        </div>
      </div>
      <div class="summary-card" :class="total_gross_profit < 0 ? 'summary-card--loss' : 'summary-card--profit'">
        <div class="summary-icon" :class="total_gross_profit < 0 ? 'summary-icon--red' : 'summary-icon--green'">
          <font-awesome-icon :icon="total_gross_profit < 0 ? 'arrow-trend-down' : 'arrow-trend-up'" />
        </div>
        <div>
          <div class="summary-label">Gross Profit</div>
          <div class="summary-value">{{ company?.currency?.symbol }}{{ $filters.formatMoney(total_gross_profit) }}</div>
        </div>
      </div>
    </div>

    <!-- Data card -->
    <div class="data-card">

      <!-- Loading -->
      <div v-if="loading" class="state-block">
        <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
        <p class="mt-3 text-muted-cell">Loading category data...</p>
      </div>

      <!-- Empty -->
      <div v-else-if="details.length === 0" class="state-block">
        <div class="state-icon">
          <font-awesome-icon icon="tags" />
        </div>
        <p class="state-title">No category data found</p>
        <p class="state-sub">
          {{ query ? 'No results match your search.' : 'Try adjusting your date range or store filter and click Apply.' }}
        </p>
      </div>

      <template v-else>

        <!-- Toolbar -->
        <div class="table-toolbar">
          <span class="table-count">
            Showing <strong>{{ pagination.from }}–{{ pagination.to }}</strong>
            of <strong>{{ pagination.total }}</strong> categories
          </span>
          <div class="toolbar-right">
            <label class="table-count">Rows:</label>
            <select v-model="tableData.length" @change="tableData.page = 1; fetchData()" class="rows-select">
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="30">30</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option :value="pagination.total">All</option>
            </select>
          </div>
        </div>

        <!-- Table -->
        <div class="table-wrap">
          <table class="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Category</th>
                <th class="text-right">Products Sold</th>
                <th class="text-right">Net Sales ({{ company?.currency?.symbol }})</th>
                <th class="text-right">Cost of Goods ({{ company?.currency?.symbol }})</th>
                <th class="text-right">Gross Profit ({{ company?.currency?.symbol }})</th>
                <th>Profit Share</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(cat, index) in details" :key="cat.id">

                <td class="text-muted-cell">{{ pagination.from + index }}</td>

                <td>
                  <div class="category-cell">
                    <div class="category-icon">
                      <font-awesome-icon icon="tag" />
                    </div>
                    <span class="category-name">{{ cat.category_name }}</span>
                  </div>
                </td>

                <td class="text-right fw-500">{{ cat.product_sold }}</td>

                <td class="text-right fw-500">{{ $filters.formatMoney(cat.net_sales) }}</td>

                <td class="text-right text-muted-cell">{{ $filters.formatMoney(cat.cost_price) }}</td>

                <td class="text-right fw-bold" :class="cat.gross_profit < 0 ? 'loss-value' : 'profit-value'">
                  {{ $filters.formatMoney(cat.gross_profit) }}
                </td>

                <!-- Profit share bar -->
                <td>
                  <div class="share-wrap">
                    <div class="share-bar">
                      <div
                        class="share-fill"
                        :style="{ width: profitShare(cat.net_sales) + '%' }"
                      ></div>
                    </div>
                    <span class="share-pct">{{ profitShare(cat.net_sales) }}%</span>
                  </div>
                </td>

              </tr>
            </tbody>

            <tfoot>
              <tr class="grand-total-row">
                <td colspan="2" class="grand-label">Grand Total</td>
                <td class="text-right fw-bold">{{ total_product_sold }}</td>
                <td class="text-right fw-bold">{{ $filters.formatMoney(total_net_sales) }}</td>
                <td class="text-right fw-bold">{{ $filters.formatMoney(total_cost_price) }}</td>
                <td class="text-right fw-bold" :class="total_gross_profit < 0 ? 'loss-value' : 'profit-value'">
                  {{ $filters.formatMoney(total_gross_profit) }}
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>

        <!-- Pagination -->
        <div class="table-footer">
          <pagination
            :pagination="pagination"
            @prev="tableData.page = pagination.currentPage - 1; fetchData()"
            @next="tableData.page = pagination.currentPage + 1; fetchData()"
          />
        </div>

      </template>
    </div>

  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';
import { reusableMixin } from '../../../lib/reusableMixin';
import Multiselect from '@vueform/multiselect';
import ReportHeaderTab from './components/reportheadertab.vue';

export default {
  name: 'SalesByCategory',

  components: { ReportHeaderTab, Multiselect },

  mixins: [reusableMixin],

  data() {
    return {
      print_content:     [],
      headerArrayColumn: ['S/N', 'Category', 'Product Sold', 'Net Sales', 'Cost Of Goods', 'Gross Profit'],
      footerRowArray:    [],

      details:     [],
      loading:     false,
      crudLoading: false,
      exportOpen:  false,
      query:       null,

      tableData: { page: 1,
        store:      'All',
        start_date: new Date(),
        end_date:   new Date(),
        draw:       0,
        length:     10,
        search:     '',
        column:     0,
        dir:        'desc',
        query:      '',
      },

      pagination: {
        lastPage: '', currentPage: '', total: '',
        lastPageUrl: '', nextPageUrl: '', prevPageUrl: '',
        from: 1, to: '',
      },

      total_product_sold: 0,
      total_cost_price:   0,
      total_net_sales:    0,
      total_gross_profit: 0,

      toast: useToast(),
    };
  },

  methods: {
    // ── Helpers ─────────────────────────────────────────────────────────
    profitShare(netSales) {
      if (!this.total_net_sales || !netSales) return 0;
      return Math.round((parseFloat(netSales) / parseFloat(this.total_net_sales)) * 100);
    },

    // ── Export ──────────────────────────────────────────────────────────
    exportPdf() {
      this.exportOpen = false;
      this.drawPdf(
        this.print_content, this.headerArrayColumn,
        'l', 'Sales-by-Category-report', 'Sales By Category Report', '', this.footerRowArray,
      );
    },

    exportCsv() {
      this.exportOpen = false;
      this.reusableMixinToCSV('sales-by-category.csv');
    },

    // ── Data ────────────────────────────────────────────────────────────
    grandTotal(items) {
      this.total_product_sold = items.reduce((s, t) => s + parseInt(t.product_sold), 0);
      this.total_cost_price   = items.reduce((s, t) => parseFloat((parseFloat(s) + parseFloat(t.cost_price)).toFixed(2)), 0);
      this.total_net_sales    = items.reduce((s, t) => parseFloat((parseFloat(s) + parseFloat(t.net_sales)).toFixed(2)), 0);
      this.total_gross_profit = items.reduce((s, t) => parseFloat((parseFloat(s) + parseFloat(t.gross_profit)).toFixed(2)), 0);

      this.footerRowArray = [
        '', 'GRAND TOTAL', this.total_product_sold,
        this.$filters.formatMoney(this.total_net_sales),
        this.$filters.formatMoney(this.total_cost_price),
        this.$filters.formatMoney(this.total_gross_profit),
      ];
    },

    dataToPrint(details) {
      this.print_content = details.map((cat, idx) => ({
        sn:            idx + 1,
        category_name: cat.category_name,
        product_sold:  cat.product_sold,
        net_sales:     this.$filters.formatMoney(cat.net_sales),
        cost_price:    this.$filters.formatMoney(cat.cost_price),
        gross_profit:  this.$filters.formatMoney(cat.gross_profit),
      }));
    },

    fetchData(url = '/report/category-sale-report') {
      this.loading     = true;
      this.crudLoading = true;
      this.tableData.query = this.query;
      this.tableData.draw++;

      axios.get(url, { params: this.tableData })
        .then(({ data }) => {
          this.details = data.data.categories.data;
          this.grandTotal(data.data.categories2);
          this.dataToPrint(this.details);
          this.configPagination(data.data.categories);
        })
        .catch(() => {
          this.toast.error('Failed to load category data. Please try again.');
        })
        .finally(() => {
          this.loading     = false;
          this.crudLoading = false;
        });
    },

    configPagination(data) {
      this.pagination.lastPage    = data.last_page;
      this.pagination.currentPage = data.current_page;
      this.pagination.total       = data.total;
      this.pagination.lastPageUrl = data.last_page_url;
      this.pagination.nextPageUrl = data.next_page_url;
      this.pagination.prevPageUrl = data.prev_page_url;
      this.pagination.from        = data.from;
      this.pagination.to          = data.to;
    },
  },

  watch: {
    query() { this.fetchData(); },
  },

  created() {
    this.fetchData();
    this.allStore();
    this.getCompany();
  },
};
</script>

<style scoped>
/* ── Page ────────────────────────────────────────────────────────────── */
.report-page { display:flex; flex-direction:column; gap:18px; }

/* ── Header ──────────────────────────────────────────────────────────── */
.page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; }
.page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub     { font-size:13px; color:#7a90b0; margin:0; }

/* ── Filters ─────────────────────────────────────────────────────────── */
.filters-wrap     { display:flex; align-items:flex-end; flex-wrap:wrap; gap:10px; }
.filter-group     { display:flex; flex-direction:column; gap:4px; }
.filter-label     { font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; }
.filter-sep       { color:#b0bdd0; font-size:16px; margin-bottom:4px; }
.filter-multiselect { min-width:150px; }
.date-input-wrap  { position:relative; display:flex; align-items:center; }
.date-icon        { position:absolute; left:10px; color:#7a90b0; font-size:12px; pointer-events:none; z-index:1; }
.date-picker      { background:none; border:none; padding-left:28px; }
.search-wrap      { position:relative; display:flex; align-items:center; }
.search-icon      { position:absolute; left:11px; color:#7a90b0; font-size:13px; pointer-events:none; }
.search-input     { padding:8px 12px 8px 33px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; width:190px; outline:none; transition:border-color .15s; }
.search-input:focus { border-color:#004AAD; }
.btn-apply        { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:8px 16px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; white-space:nowrap; }
.btn-apply:hover:not(:disabled) { background:#003b8e; }
.btn-apply:disabled { opacity:.55; cursor:not-allowed; }
.btn-outline      { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:8px 14px; border-radius:8px; cursor:pointer; transition:background .15s; white-space:nowrap; }
.btn-outline:hover { background:#f4f7fb; }
.dropdown         { position:relative; }
.dropdown-menu    { position:absolute; top:calc(100% + 6px); right:0; background:#fff; border:1px solid #e4eaf2; border-radius:10px; box-shadow:0 8px 24px rgba(0,0,0,.1); padding:6px; min-width:160px; z-index:200; display:none; }
.dropdown-menu.show { display:block; }
.dropdown-item    { display:flex; align-items:center; gap:8px; padding:8px 12px; font-size:13px; border-radius:7px; color:#1a2b45; cursor:pointer; text-decoration:none; }
.dropdown-item:hover { background:#f4f7fb; }
.me-2 { margin-right:8px; }

/* ── Summary cards ───────────────────────────────────────────────────── */
.summary-grid         { display:grid; grid-template-columns:repeat(4,1fr); gap:14px; }
.summary-card         { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:18px 20px; display:flex; align-items:center; gap:16px; }
.summary-card--profit { border-color:#bbf7d0; background:#f0fdf4; }
.summary-card--loss   { border-color:#fecaca; background:#fff5f5; }
.summary-icon         { width:44px; height:44px; border-radius:12px; display:flex; align-items:center; justify-content:center; font-size:18px; flex-shrink:0; }
.summary-icon--blue   { background:rgba(0,74,173,.1); color:#004AAD; }
.summary-icon--indigo { background:#eef2ff; color:#4f46e5; }
.summary-icon--amber  { background:#fff7ed; color:#e08c00; }
.summary-icon--green  { background:#f0fdf4; color:#16a34a; }
.summary-icon--red    { background:#fff5f5; color:#e03535; }
.summary-label        { font-size:12px; font-weight:600; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; margin-bottom:5px; }
.summary-value        { font-size:20px; font-weight:700; color:#1a2b45; }
.summary-card--profit .summary-value { color:#16a34a; }
.summary-card--loss   .summary-value { color:#e03535; }

/* ── Data card ───────────────────────────────────────────────────────── */
.data-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }

/* ── States ──────────────────────────────────────────────────────────── */
.state-block { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
.state-icon  { width:60px; height:60px; border-radius:16px; background:rgba(0,74,173,.08); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
.state-title { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
.state-sub   { font-size:13px; color:#7a90b0; margin:0; }
.text-muted-cell { color:#4a5e78; }
.mt-3 { margin-top:12px; }

/* ── Table toolbar ───────────────────────────────────────────────────── */
.table-toolbar  { display:flex; align-items:center; justify-content:space-between; padding:14px 20px; border-bottom:1px solid #e4eaf2; flex-wrap:wrap; gap:10px; }
.table-count    { font-size:13px; color:#7a90b0; }
.table-count strong { color:#1a2b45; }
.toolbar-right  { display:flex; align-items:center; gap:8px; }
.rows-select    { border:1px solid #dde5f0; border-radius:7px; padding:5px 10px; font-size:13px; color:#1a2b45; background:#f8fafc; outline:none; cursor:pointer; }

/* ── Table ───────────────────────────────────────────────────────────── */
.table-wrap  { overflow-x:auto; }
.data-table  { width:100%; border-collapse:collapse; }
.data-table thead tr { background:#f8fafc; }
.data-table thead th { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:12px 16px; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
.data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .12s; }
.data-table tbody tr:last-child { border-bottom:none; }
.data-table tbody tr:hover { background:#f8fafc; }
.data-table tbody td { padding:12px 16px; font-size:13px; vertical-align:middle; }
.text-right   { text-align:right; }
.fw-500       { font-weight:500; }
.fw-bold      { font-weight:700; }
.profit-value { color:#16a34a; }
.loss-value   { color:#e03535; }

/* ── Category cell ───────────────────────────────────────────────────── */
.category-cell { display:flex; align-items:center; gap:10px; }
.category-icon { width:30px; height:30px; border-radius:8px; background:#eef2ff; color:#4f46e5; display:flex; align-items:center; justify-content:center; font-size:12px; flex-shrink:0; }
.category-name { font-size:13px; font-weight:500; color:#1a2b45; }

/* ── Share bar ───────────────────────────────────────────────────────── */
.share-wrap { display:flex; align-items:center; gap:10px; min-width:120px; }
.share-bar  { flex:1; height:6px; background:#f0f4f8; border-radius:99px; overflow:hidden; }
.share-fill { height:100%; background:linear-gradient(90deg, #4f46e5, #818cf8); border-radius:99px; transition:width .4s ease; }
.share-pct  { font-size:12px; font-weight:600; color:#7a90b0; min-width:36px; text-align:right; }

/* ── Grand total ─────────────────────────────────────────────────────── */
.grand-total-row    { background:#f8fafc; border-top:2px solid #e4eaf2; }
.grand-total-row td { padding:13px 16px; font-size:13px; }
.grand-label        { font-size:12px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; }

/* ── Footer ──────────────────────────────────────────────────────────── */
.table-footer { padding:14px 20px; border-top:1px solid #e4eaf2; display:flex; justify-content:flex-end; }

/* ── Responsive ──────────────────────────────────────────────────────── */
@media (max-width:1024px) { .summary-grid { grid-template-columns:repeat(2,1fr); } }
@media (max-width:768px)  {
  .page-header, .filters-wrap { flex-direction:column; align-items:stretch; }
  .filter-sep     { display:none; }
  .search-input   { width:100%; }
  .summary-grid   { grid-template-columns:1fr 1fr; }
  .filter-multiselect { width:100%; }
  .share-wrap     { min-width:80px; }
}
@media (max-width:480px)  { .summary-grid { grid-template-columns:1fr; } }
</style>